import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTeamUsers } from '@/api/supervisor';
import { useDebounce } from '@/hooks/use-debounce';
import { LoadingSpinner } from '@/components/shared/loading-spinner';
import { ErrorMessage } from '@/components/shared/error-message';
import { EmptyState } from '@/components/shared/empty-state';
import { Pagination } from '@/components/shared/pagination';
import { cn } from '@/lib/utils';
import { SearchInput } from '@/components/shared/search-input';
import { TeamCards } from './_components/team-cards';
import { ROLE_LABELS } from './_components/team-labels';

export function TeamPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const { data, isLoading, error } = useTeamUsers({
    page,
    per_page: 20,
    search: debouncedSearch || undefined,
  });
  const navigate = useNavigate();

  const users = data?.data ?? [];
  const pagination = data?.meta?.pagination;

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message="No se pudo cargar la lista del equipo." />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Mi Equipo</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Usuarios de tu departamento y su progreso de capacitación.
        </p>
      </div>

      {/* Search */}
      <SearchInput
        value={search}
        onChange={(value) => {
          setSearch(value);
          setPage(1);
        }}
        placeholder="Buscar por nombre o email..."
      />

      {!users.length ? (
        <EmptyState
          title="Sin miembros"
          description={
            search ? 'No se encontraron resultados.' : 'No tienes miembros en tu equipo.'
          }
        />
      ) : (
        <>
          <div className="hidden overflow-x-auto rounded-xl border border-border md:block">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Nombre</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Email</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Rol</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Posiciones
                  </th>
                  <th className="px-4 py-3 text-center font-medium text-muted-foreground">
                    Estado
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {users.map((user) => (
                  <tr
                    key={user.id}
                    className="cursor-pointer hover:bg-muted/30"
                    onClick={() => navigate(`/instructor/reports?user=${user.id}`)}
                  >
                    <td className="px-4 py-3 font-medium text-card-foreground">
                      {user.first_name} {user.last_name}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{user.email}</td>
                    <td className="px-4 py-3">
                      <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium">
                        {ROLE_LABELS[user.role] ?? user.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {user.user_positions?.map((up) => up.position.name).join(', ') || '—'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={cn(
                          'inline-block h-2 w-2 rounded-full',
                          user.is_active ? 'bg-green-500' : 'bg-gray-400',
                        )}
                        title={user.is_active ? 'Activo' : 'Inactivo'}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <TeamCards
            users={users}
            pagination={pagination}
            onPageChange={setPage}
            onOpen={(user) => navigate(`/instructor/reports?user=${user.id}`)}
          />

          {pagination && (
            <div className="hidden md:block">
              <Pagination meta={pagination} onPageChange={setPage} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
